import { Hidden } from '@mui/material'
import Image from 'next/image'
import Link from 'next/link'
import React, { useEffect, useState } from 'react'
import { ContentTimestamp, getContentDate } from 'src/components/ContentTimestamp/ContentTimestamp'
import { ContentWriter } from 'src/components/ContentWriter/ContentWriter'
import { HtmlContentViewer } from 'src/components/global/HtmlContentViewer/HtmlContentViewer'
import { PremiumContentIcon } from 'src/components/global/PremiumContentIcon/PremiumContentIcon'
import { SignUpModal } from 'src/components/global/SignUpModal'
import { ThemeOnly } from 'src/components/global/ThemeOnly'
import { InsightCardTags } from 'src/components/Insights/InsightCardTags'
import {
  IContentItemFragment,
  IContentItemWriterFragment,
  IEnum_Content_Contenttype,
} from 'src/graphql/generated/hooks'
import { useDefaultLogo } from 'src/hooks/useDefaultLogo'
import { ContentDataFragment, isContentItem, isContentListItemFragment } from 'src/utils/ContentDataFragment'
import { getContentDefaultImageUrl } from 'src/utils/getContentDefaultImageUrl'
import { highlightKeywordsInHtml } from 'src/utils/keywordHighlighter'
import { type NormalizedWriter, normalizeWritersList } from 'src/utils/normalizeContentWriters'
export interface ContentListItemProps {
  item: ContentDataFragment
  showDocumentType?: boolean
  vendor?: boolean
  openTooltipCardId?: string | null
  setOpenTooltipCardId: (id: string | null) => void
  subjectMatterPopup?: number
  contentType?: IEnum_Content_Contenttype
  searchKeyword?: string
  fromPage?: string
}

export const ContentListItem: React.FC<ContentListItemProps> = ({
  item,
  showDocumentType = false,
  vendor = false,
  openTooltipCardId,
  setOpenTooltipCardId,
  subjectMatterPopup,
  contentType: contentTypeFromProps,
  searchKeyword,
  fromPage = '',
}) => {
  const [showSignUp, setShowSignUp] = useState(false)

  const handleCloseSignUp = () => {
    setShowSignUp(false)
  }

  const toTagString = (val: any): string => {
    if (!val) return ''
    if (typeof val === 'string') return val

    // Strapi relation response collection: { data: [{ attributes: { name }}, ...] }
    const data = val?.data
    if (Array.isArray(data)) {
      return data
        .map((el: any) => el?.attributes?.name)
        .filter((name: any) => typeof name === 'string' && name.trim() !== '')
        .join(', ')
    }
    return ''
  }

  const subjectMatters = isContentListItemFragment(item)
    ? item?.subjectMatters
    : isContentItem(item)
      ? item.subjectMatters
      : ''
  const themes = isContentListItemFragment(item) ? item?.themes : isContentItem(item) ? item.themes : ''
  const vendorTopics = isContentListItemFragment(item)
    ? item?.vendorTopics
    : isContentItem(item)
      ? item.vendorTopics
      : ''

  const subjectMattersText = toTagString(subjectMatters)
  const themesText = toTagString(themes)
  const vendorTopicsText = toTagString(vendorTopics)

  let contentTags: any = []
  if (contentTypeFromProps === IEnum_Content_Contenttype.KnowHow) {
    contentTags = [subjectMattersText]
      // .map((val) => (typeof val === 'string' ? val : ''))
      .filter((str) => str && str.trim() !== '')
      .join(', ')
  } else if (contentTypeFromProps === IEnum_Content_Contenttype.LegaltechResourcesForLawyers) {
    contentTags = [themesText]
      // .map((val) => (typeof val === 'string' ? val : ''))
      .filter((str) => str && str.trim() !== '')
      .join(', ')
  } else if (contentTypeFromProps === IEnum_Content_Contenttype.VendorContent) {
    contentTags = [vendorTopicsText]
      // .map((val) => (typeof val === 'string' ? val : ''))
      .filter((str) => str && str.trim() !== '')
      .join(', ')
  } else {
    contentTags = [subjectMattersText, themesText, vendorTopicsText]
      // .map((val) => (typeof val === 'string' ? val : ''))
      .filter((str) => str && str.trim() !== '')
      .join(', ')
  }

  const subjectMatterIds = isContentListItemFragment(item)
    ? item?.subjectMatterIds || []
    : isContentItem(item)
      ? item.subjectMatterIds
      : []
  const themeIds = isContentListItemFragment(item) ? item?.themeIds : isContentItem(item) ? item.themeIds : []
  const vendorTopicIds = isContentListItemFragment(item)
    ? item?.vendorTopicIds
    : isContentItem(item)
      ? item.vendorTopicIds || []
      : []

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const makeLinks = (linkTo: string, subjectMatterArray: any | undefined, subjectMatterIDs: any) => {
    // console.log('content-list-item::subjectMatterArray ', subjectMatterArray)
    let goto = 'subject_matter_'
    if (!subjectMatterArray || subjectMatterArray === '' || subjectMatterArray === undefined) {
      return []
    }
    if (linkTo === 'subject_matter') {
      goto = 'know-how/?subject=subject_matter_'
    } else if (linkTo === 'theme') {
      goto = 'legaltech-resources-for-lawyers/?subject=theme_'
    } else if (linkTo === 'vendor_topic') {
      goto = 'resources-for-solution-providers/?vendorTopic='
    }
    if (typeof subjectMatterArray === 'string') {
      return subjectMatterArray?.split(',').map((name: string, index: number) => ({
        name: name.trim(),
        id: subjectMatterIDs?.[index],
        link: `${goto}${subjectMatterIDs?.[index]}`,
      }))
    } else {
      const data =
        subjectMatterArray?.map((el: any, index: number) => ({
          name: el?.name?.trim() || el?.attributes?.name?.trim() || '',
          id: subjectMatterIDs?.[index],
          link: `${goto}${subjectMatterIDs?.[index]}`,
        })) || []
      return subjectMatterArray && subjectMatterArray?.length === 0 ? [] : data
    }
  }
  const subjectMatterLiks = makeLinks('subject_matter', subjectMatters, subjectMatterIds)
  const themesLiks = makeLinks('theme', themes, themeIds)
  const vendorTopicsLiks = makeLinks('vendor_topic', vendorTopics, vendorTopicIds)

  let contentTagsLink = [subjectMatterLiks, themesLiks, vendorTopicsLiks].flatMap((item) => item)

  if (contentTypeFromProps === IEnum_Content_Contenttype.KnowHow) {
    contentTagsLink = [subjectMatterLiks].flatMap((item) => item)
  } else if (contentTypeFromProps === IEnum_Content_Contenttype.LegaltechResourcesForLawyers) {
    contentTagsLink = [themesLiks].flatMap((item) => item)
  } else if (contentTypeFromProps === IEnum_Content_Contenttype.VendorResource) {
    contentTagsLink = [vendorTopicsLiks].flatMap((item) => item)
  } else {
    contentTagsLink = [subjectMatterLiks, themesLiks, vendorTopicsLiks].flatMap((item) => item)
  }

  const contentType = isContentListItemFragment(item)
    ? item?.contentType
    : isContentItem(item)
      ? item.contentType
      : IEnum_Content_Contenttype.VendorResource
  const documentType = isContentListItemFragment(item) ? item?.documentType : item.documentType
  const title = (isContentListItemFragment(item) ? item?.title : item.title) || ''
  const defaultImageUrl = getContentDefaultImageUrl(item)
  const shouldShowDefaultImage =
    contentType &&
    [
      IEnum_Content_Contenttype.Blog,
      IEnum_Content_Contenttype.IndustryAnalysis,
      IEnum_Content_Contenttype.VendorResource,
      IEnum_Content_Contenttype.KnowHow,
      IEnum_Content_Contenttype.LegaltechResourcesForLawyers,
      IEnum_Content_Contenttype.BuyersGuide,
      IEnum_Content_Contenttype.PremiumResource,
    ].includes(contentType)
  const snippet =
    (isContentListItemFragment(item) ? item?.snippet : isContentItem(item) ? item.snippet : item.summary) || ''

  let displayContent = snippet
  let displayTitle = title

  // Apply keyword highlighting if searchKeyword is provided
  // This preserves existing functionality for snippet highlighting
  if (searchKeyword && searchKeyword.trim()) {
    // Split by commas only to preserve phrases with spaces
    const keywords = searchKeyword
      .split(',')
      .map((k) => k.trim())
      .filter((k) => k.length > 0)
    if (keywords.length > 0) {
      // Always highlight snippet (existing functionality - must not break)
      displayContent = highlightKeywordsInHtml(snippet, keywords)
      // Additionally highlight title (new functionality - same logic as snippet)
      displayTitle = highlightKeywordsInHtml(title, keywords)
    }
  }

  const writerProp: unknown = isContentListItemFragment(item)
    ? item?.writer
    : isContentItem(item)
      ? (() => {
          const ext = item as IContentItemFragment & { writers?: IContentItemWriterFragment[] | null }
          return ext.writers?.length ? ext.writers : item.writer
        })()
      : item.author
  const hasWriters = normalizeWritersList(writerProp).length > 0

  // Construct URL with keyword parameter if available
  const baseUrl = isContentListItemFragment(item)
    ? item?.slug
      ? item.contentType === IEnum_Content_Contenttype.IndustryAnalysis
        ? `/industry-analysis/${item?.slug}`
        : `/contents/${item?.slug}`
      : null
    : isContentItem(item)
      ? `/contents/${item.slug}`
      : item.link

  // Add keyword parameter to content URLs if searchKeyword is provided
  // Apply to both /contents/ and /industry-analysis/ URLs

  const extraParams = baseUrl && (baseUrl.startsWith('/contents/') || baseUrl.startsWith('/industry-analysis/'))
  const url =
    baseUrl && searchKeyword && searchKeyword.trim() && extraParams
      ? `${baseUrl}${baseUrl.includes('?') ? '&' : '?'}keyword=${encodeURIComponent(searchKeyword)}`
      : baseUrl

  /* const url =
    baseUrl &&
      searchKeyword &&
      searchKeyword.trim() &&
      (baseUrl.startsWith('/contents/') || baseUrl.startsWith('/industry-analysis/'))
      ? `${baseUrl}${baseUrl.includes('?') ? '&' : '?'}keyword=${encodeURIComponent(searchKeyword)}`
      : baseUrl */

  const isInternalLink = isContentListItemFragment(item) || isContentItem(item)
  const logo = useDefaultLogo(defaultImageUrl)

  const logoContent = (
    <>
      {shouldShowDefaultImage && defaultImageUrl ? (
        <Image
          className="bg-white rounded-[.5rem] drop-shadow-md flex items-center justify-center w-[80px] h-[80px] min-w-[80px] min-h-[80px]"
          src={defaultImageUrl}
          alt={title}
          width={80}
          height={80}
          priority
        />
      ) : contentType ? (
        <PremiumContentIcon contentType={contentType} size="lg" defaultImage={defaultImageUrl} />
      ) : (
        <Image
          className="bg-white rounded-[.5rem] drop-shadow-md flex items-center justify-center w-[80px] h-[80px] min-w-[80px] min-h-[80px]"
          src={logo}
          alt={title}
          width={80}
          height={80}
          priority
        />
      )}
    </>
  )

  useEffect(() => {
    setOpenTooltipCardId(null)
  }, [subjectMatterPopup])

  const documentTypeText = (documentType: string) => {
    const text = (documentType || '').replace(/_/g, ' ').trim()
    return text === 'Blog' ? 'Editorial' : text
  }

  // ✅ Only content fragments have `documentId` (external vendor resources do not)
  const cardId = isContentListItemFragment(item) || isContentItem(item) ? item.documentId : ''

  return (
    <div className="flex flex-row items-center pt-[14px] pb-[14px]">
      <div className="flex flex-row w-full md:items-center">
        <div className="mr-[30px]">
          {url && (
            <Link
              prefetch={false}
              className="text-left"
              href={url}
              target={isInternalLink ? undefined : '_blank'}
              title={title}
            >
              {logoContent}
            </Link>
          )}
          {!url && <div className="text-left">{logoContent}</div>}
        </div>

        <div className="grow content-list-cards">
          <div className="w-full flex md:flex-row flex-col justify-between">
            <div className="w-full flex flex-col justify-between">
              <div className="w-full flex flex-col md:flex-row md:justify-between md:items-center gap-2 md:gap-0">
                <div className="cursor-pointer text-[13px] text-textdark font-secondary font-medium leading-[20px]">
                  {showDocumentType ? documentTypeText(documentType || '') : documentTypeText(contentType || '')}
                </div>
                <div className="flex flex-wrap items-center gap-2 md:gap-0">
                  {!vendor && hasWriters && (
                    <ContentWriter writer={writerProp as NormalizedWriter | NormalizedWriter[]} />
                  )}
                  {getContentDate(item) && (
                    <div className="flex items-center">
                      <div className="md:mr-3">
                        <ContentTimestamp content={item} fromPage={fromPage} />
                      </div>
                    </div>
                  )}

                  {vendor && (
                    <div className="bg-fog px-[.4rem] py-[.2rem] text-textdark font-bold rounded-[6px] text-[.8rem] tracking-widest leading-5 h-full">
                      Vendor Content
                    </div>
                  )}
                </div>
              </div>
              {url ? (
                <Link
                  prefetch={false}
                  className="text-left text-line-2 cursor-pointer text-[18px] text-textdark font-secondary font-bold mb-1.5"
                  href={url}
                  target={isInternalLink ? undefined : '_blank'}
                >
                  <span dangerouslySetInnerHTML={{ __html: displayTitle }} />
                </Link>
              ) : (
                <div
                  className="text-left text-line-2 cursor-pointer text-[18px] text-textdark font-secondary font-bold mb-1.5"
                  dangerouslySetInnerHTML={{ __html: displayTitle }}
                />
              )}
            </div>
          </div>
          <HtmlContentViewer
            className="md:pr-[2rem] line-clamp-2 !text-[14px] leading-[18px] !font-medium remove-p-margin"
            html={displayContent}
          />
          <ThemeOnly theme="legaltech">
            <div className="gap-[8px] mt-[6px] mb-[2px] h-[22px] relative">
              {contentTags && (
                <>
                  <Hidden mdDown>
                    <InsightCardTags
                      key={`${cardId}-ContentListItem`}
                      cardId={cardId}
                      tagItems={contentTags}
                      contentTagsLink={contentTagsLink}
                      openTooltipCardId={openTooltipCardId}
                      setOpenTooltipCardId={setOpenTooltipCardId}
                      containerSize={675}
                      subjectMatterPopup={subjectMatterPopup}
                      onWhichPage={'content-list-item'}
                    />
                  </Hidden>
                  <Hidden mdUp>
                    <div className="content-list-mobile">
                      <InsightCardTags
                        key={`${cardId}-ContentListItem`}
                        cardId={cardId}
                        tagItems={contentTags}
                        contentTagsLink={contentTagsLink}
                        openTooltipCardId={openTooltipCardId}
                        setOpenTooltipCardId={setOpenTooltipCardId}
                        containerSize={280}
                        subjectMatterPopup={subjectMatterPopup}
                        onWhichPage={'content-list-item'}
                      />
                    </div>
                  </Hidden>
                </>
              )}
            </div>
          </ThemeOnly>
        </div>
      </div>
      <SignUpModal isOpen={showSignUp} onClose={handleCloseSignUp} />
    </div>
  )
}
